import { useEffect, useRef, useState } from "react";
import { KeyRound, Pencil, Trash2, X } from "lucide-react";
import { useUser } from "../../../contexts/UserContext";
import { Button } from "../../../Components/Buttons";
import { FormInput, FormImage } from "../../../Components/Form";
import BreadCrumb from "../../../Components/BreadCrumb";
import { getUserFromToken, ToastMessage } from "../../../Components/Utils";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { useUpdateUserPasswordMutation, useUpdateUserProfileMutation } from "../../../stores/api/users.api";
import { userProfileHelper } from "../../../stores/api/helpers/users.helper";
import { handleError } from "../../../stores/components/handleError";
import { useFetchPermissionsMutation } from "../../../stores/api/auth.api";
import payloadHelper from "../../../stores/api/helpers/payload.helper";
import { user_profile_image_url } from "../../../Components/ImageUrls";
/* ── Modal shell ─────────────────────────────────── */
const Modal = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-xl shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-slate-700">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{title}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 transition">
            <X size={16} className="text-gray-500" />
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
};

const Profile = () => {
  const { user, setUser } = useUser();

  const navigate = useNavigate();

  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarModal, setAvatarModal] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarLoading, setAvatarLoading] = useState(false);

  const [infoForm, setInfoForm] = useState({ name: user?.name || "", email: user?.email || "", image: null });
  const [infoLoading, setInfoLoading] = useState(false);
  useEffect(() => {
    setInfoForm({ name: user?.name || "", email: user?.email || "", image: null });
  }, [user?.id]);
  /* password modal */
  const [pwdModal, setPwdModal] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current_password: "", password: "", confirm_password: "" });
  const [pwdLoading, setPwdLoading] = useState(false);

  const defaultAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent((user?.name || "User").split(" ").slice(0, 2).join("+"))}&background=F97316&color=fff&size=128`;
  const displayAvatar = avatarPreview || user_profile_image_url + user?.user_image || defaultAvatar;

  const [updateUserProfile, { isLoading: isProfileUpdating }] = useUpdateUserProfileMutation();
  const [updateUserPassword, { isLoading: isPasswordUpdating }] = useUpdateUserPasswordMutation();
  const [fetchPermissions, { isLoading: permsLoading }] = useFetchPermissionsMutation();

  const handleAvatarSave = async () => {
    if (!avatarFile) { setAvatarModal(false); return; }
    setAvatarLoading(true);
    try {
      const response = await updateUserProfile(userProfileHelper.forUpdate(user?.id, { ...infoForm, image: avatarFile })).unwrap();
      if (response.token) {
        await handleUserDataFetch(response.token);
      }
      console.log("Profile updated successfully", user);
      ToastMessage({ type: "success", title: "Updated", message: "Profile photo updated." });
      setAvatarModal(false);
    } catch (error) {
      handleError(error);
    } finally {
      setAvatarLoading(false);
    }
  };

  const handleInfoSave = async (e) => {
    e.preventDefault();
    if (!infoForm.name.trim()) {
      ToastMessage({ type: "error", title: "Required", message: "Name cannot be empty." });
      return;
    }
    setInfoLoading(true);
    try {
      const response = await updateUserProfile(userProfileHelper.forUpdate(user?.id, infoForm)).unwrap();
      if (response.token) {
        await handleUserDataFetch(response.token);
      }
      ToastMessage({ type: "success", title: "Saved", message: "Profile updated." });
    } catch (error) {
      handleError(error);
    } finally {
      setInfoLoading(false);
    }
  };
  const handleUserDataFetch = async (token) => {
    if (token) {
      Cookies.set("token", token, {
        expires: 7,
      });
      const getUserData = getUserFromToken(token);
      const getPermission = await fetchPermissions(
        payloadHelper.forFetchPermissions(getUserData.role_id)
      ).unwrap();
      if (getPermission) {
        localStorage.setItem("permissions", JSON.stringify(getPermission));
      }
    }
  }
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!pwdForm.current_password) {
      ToastMessage({ type: "error", title: "Required", message: "Current password required." });
      return;
    }
    if (pwdForm.password.length < 6) {
      ToastMessage({ type: "error", title: "Too short", message: "New password min 6 characters." });
      return;
    }
    if (pwdForm.password !== pwdForm.confirm_password) {
      ToastMessage({ type: "error", title: "Mismatch", message: "Passwords do not match." });
      return;
    }
    setPwdLoading(true);
    try {
      const passwordPayload = {
        current_password: pwdForm.current_password,
        password: pwdForm.password,
      }
      console.log(passwordPayload);
      const response = await updateUserPassword(userProfileHelper.forPasswordUpdate(user?.id, passwordPayload)).unwrap();
      setPwdForm({ current_password: "", password: "", confirm_password: "" });
      setPwdModal(false);
      ToastMessage({ type: "success", title: "Done", message: "Password changed." });
    } catch(error) {
      handleError(error, "Password change failed. Please check your current password and try again.");
    } finally {
      setPwdLoading(false);
    }
  };



  return (
    <div className="p-4 space-y-6">
      <BreadCrumb title="My Profile" />

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-8 space-y-8">

        {/* ── Avatar ── */}
        <div className="flex flex-col items-center gap-3">
          <div className="relative">
            <img
              src={displayAvatar}
              alt="avatar"
              className="w-24 h-24 rounded-full object-cover ring-4 ring-green-100 dark:ring-green-500/20"
            />
            <button
              onClick={() => setAvatarModal(true)}
              className="absolute bottom-0 right-0 w-7 h-7 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center shadow transition"
            >
              <Pencil size={12} />
            </button>
          </div>
          <div className="text-center">
            <p className="text-base font-semibold text-gray-800 dark:text-gray-100">{user?.name || "User"}</p>
            <p className="text-sm text-gray-400">{user?.email || ""}</p>
          </div>
        </div>

        <hr className="border-gray-100 dark:border-slate-700" />
        <form onSubmit={handleInfoSave} className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Account Info</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              isRequired
              label="Full Name"
              name="name"
              value={infoForm.name}
              onChange={(val) => setInfoForm((p) => ({ ...p, name: val }))}
              placeholder="Your full name"
            />
            <FormInput
              label="Email"
              name="email"
              value={infoForm.email}
              onChange={(val) => setInfoForm((p) => ({ ...p, email: val }))}
              placeholder="your@email.com"
            />
          </div>

          <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
            <div className="flex gap-2">

              <Button
                type="button"
                onClick={() => setPwdModal(true)}
                className="!bg-gray-100 !text-gray-700 hover:!bg-gray-200 dark:!bg-slate-700 dark:!text-gray-200 shadow"
              >
                <KeyRound size={14} className="mr-1.5" />
                Change Password
              </Button>
              <Button
                type="submit"
                className="!bg-green-500 !text-white hover:!bg-green-600 shadow"
                loading={infoLoading}
                disabled={infoLoading}
                loadingText="Saving..."
              >
                Save Changes
              </Button>
            </div>

          </div>
        </form>
      </div>

      {/* ── Avatar upload modal ── */}
      <Modal open={avatarModal} onClose={() => setAvatarModal(false)} title="Update Profile Photo">
        <div className="space-y-4">
          <FormImage
            label="Profile Photo"
            initialPreviewUrl={displayAvatar}
            onChange={(file) => {
              setAvatarFile(file);
              if (file) setAvatarPreview(URL.createObjectURL(file));
            }}
          />
          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              onClick={() => setAvatarModal(false)}
              className="!bg-gray-100 !text-gray-700 hover:!bg-gray-200 dark:!bg-slate-700 dark:!text-gray-200"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleAvatarSave}
              className="!bg-green-500 !text-white hover:!bg-green-600"
              loading={avatarLoading}
              disabled={avatarLoading}
              loadingText="Uploading..."
            >
              Save Photo
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Change password modal ── */}
      <Modal open={pwdModal} onClose={() => setPwdModal(false)} title="Change Password">
        <form onSubmit={handleChangePassword} className="space-y-4">
          <FormInput
            isRequired
            label="Current Password"
            name="current_password"
            type="password"
            value={pwdForm.current_password}
            onChange={(val) => setPwdForm((p) => ({ ...p, current_password: val }))}
            placeholder="••••••••"
          />
          <FormInput
            isRequired
            label="New Password"
            name="password"
            type="password"
            value={pwdForm.password}
            onChange={(val) => setPwdForm((p) => ({ ...p, password: val }))}
            placeholder="••••••••"
          />
          <FormInput
            isRequired
            label="Confirm New Password"
            name="confirm_password"
            type="password"
            value={pwdForm.confirm_password}
            onChange={(val) => setPwdForm((p) => ({ ...p, confirm_password: val }))}
            placeholder="••••••••"
          />
          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              onClick={() => setPwdModal(false)}
              className="!bg-gray-100 !text-gray-700 hover:!bg-gray-200 dark:!bg-slate-700 dark:!text-gray-200"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="!bg-green-500 !text-white hover:!bg-green-600"
              loading={pwdLoading}
              disabled={pwdLoading}
              loadingText="Updating..."
            >
              Update Password
            </Button>
          </div>
        </form>
      </Modal>

    </div>
  );
};

export default Profile;
